import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Animated,
  AppState,
  Easing,
  Linking,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import Icon from '../components/ui/Icon';
import Text from '../components/ui/Text';
import ConfirmSheet from '../components/ui/ConfirmSheet';
import { Booking } from '../data/bookings';
import RideTrackingMap from '../components/ride/RideTrackingMap';
import { contextFor, SOS_HOLD_MS, sosChannels } from '../data/sos';
import { locate } from '../services/fix';
import {
  drainQueue,
  draftFor,
  fetchActive,
  raise as raiseAlert,
  standDown as standDownAlert,
  SOS_RETRY_MS,
  type ServerSosAlert,
  type SosDelivery,
} from '../services/sos';
import {
  colors,
  hairline,
  radii,
  screenPadding,
  spacing,
  useResponsive,
} from '../theme';

/**
 * The alarm.
 *
 * ---------------------------------------------------------------------------
 * PRESS AND HOLD, AND THAT IS THE WHOLE GATE
 * ---------------------------------------------------------------------------
 * No dialog, no reason, no form. The ring fills over `SOS_HOLD_MS` and on the
 * way past the end it is sent. Lifting early cancels and says so, so a
 * half-press is never ambiguous.
 *
 * A hold rather than a tap because this screen can be reached by accident and
 * a pocket cannot hold a button for 1.6 seconds. A hold rather than a
 * confirmation dialog because a dialog is a second decision at the moment
 * somebody is least able to make one - and because "Are you sure?" is exactly
 * the question this feature must never ask.
 *
 * ---------------------------------------------------------------------------
 * THREE DELIVERY STATES, AND THE SCREEN NEVER CONFLATES THEM
 * ---------------------------------------------------------------------------
 * The alarm is dispatched for real - `POST /sos/alerts`, with a live GPS fix
 * where one can be had inside `FIX_DEADLINE_MS` - and what the screen says
 * depends on how far it got:
 *
 *   queued        on the phone, not delivered. Still trying, and says so.
 *   sent          the server has it. Nobody has read it yet.
 *   acknowledged  a person has it.
 *
 * "Sent" and "somebody is reading this" are different promises, and collapsing
 * them is the same mistake as a refund screen that says "Refunded" when the
 * bank has not paid out yet. `sent` deliberately does NOT say help is on the
 * way; only `acknowledged` does.
 *
 * ---------------------------------------------------------------------------
 * THE ALARM IS WRITTEN TO DISK BEFORE IT IS SENT
 * ---------------------------------------------------------------------------
 * `services/sos.ts` queues every press before the request leaves, retries on a
 * timer while this screen is open, and drains on next launch and on
 * foreground. So a press with no signal is not lost, and the button never
 * blocks on the network - `raise()` cannot reject.
 *
 * What that still is NOT is a headless background send. A phone with no signal
 * that is locked and never reopened does not deliver its alarm, because that
 * needs a native task and a rebuild. The screen says "still trying" in that
 * state rather than implying anything was received.
 *
 * The 112 row dials for real, through `Linking`, and is the answer whenever
 * the platform itself is the thing that is failing.
 */

type Props = {
  onBack: () => void;
  /** The visit this is about, where the alarm was raised from one. */
  booking?: Booking;
  /**
   * The trip this is about, where the alarm was raised from inside a car.
   *
   * Never set at the same time as `booking` for a customer: a customer's ride
   * is a standalone `customer_trip` with no booking behind it, which is
   * exactly why it needed its own way in. Its presence changes two things -
   * the alarm carries the vehicle and the driver, and once it is live this
   * screen follows the car.
   */
  ride?: { id: string; driverName?: string | null; plate?: string | null };
};

type Stage = 'idle' | 'active' | 'stood_down';

function SosScreen({ onBack, booking, ride }: Props) {
  const resp = useResponsive();
  const [stage, setStage] = useState<Stage>('idle');
  const [standDownOpen, setStandDownOpen] = useState(false);

  /** How far the alarm got. Only meaningful while `stage === 'active'`. */
  const [delivery, setDelivery] = useState<SosDelivery>('queued');
  /** The row on the server, once there is one. Null while only queued. */
  const [alert, setAlert] = useState<ServerSosAlert | null>(null);

  /** 0 → 1 across the hold. Drives the ring and nothing else. */
  const hold = useRef(new Animated.Value(0)).current;
  const holding = useRef<Animated.CompositeAnimation | null>(null);

  /*
   * Guards a state update against an unmount. Everything below is async - a
   * GPS fix, a POST, a poll - and the customer can leave the screen mid-flight
   * without cancelling the alarm, which continues in the service layer as it
   * should.
   */
  const live = useRef(true);
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);

  const context = contextFor(booking);

  /**
   * Send it.
   *
   * The stage flips to `active` FIRST, before the fix and before the request.
   * Somebody who has held the button through has raised the alarm; making them
   * watch a spinner to find out whether the network agreed would be the wrong
   * answer even if it were fast. The delivery state underneath then tells them
   * the truth about where it got to.
   */
  const raise = useCallback(async () => {
    setStage('active');
    setDelivery('queued');
    setAlert(null);

    /*
     * Bounded by `FIX_DEADLINE_MS` and never throws - worst case it resolves
     * null and the alarm goes with the address snapshot instead, which is
     * exactly what it carried before there was a fix at all.
     */
    const fix = await locate();

    const state = await raiseAlert(
      draftFor(booking, context, fix ?? undefined, ride),
    );

    if (!live.current) {
      return;
    }
    setDelivery(state.delivery);
    setAlert(state.alert ?? null);
  }, [booking, context, ride]);

  const startHold = useCallback(() => {
    holding.current?.stop();
    hold.setValue(0);
    const run = Animated.timing(hold, {
      toValue: 1,
      duration: SOS_HOLD_MS,
      easing: Easing.linear,
      // Width cannot be driven natively, and the ring is a width - see below.
      useNativeDriver: false,
    });
    holding.current = run;
    run.start(({ finished }) => {
      if (finished) {
        // `raise` owns its own failures and cannot reject - see `services/sos.ts`.
        void raise();
      }
    });
  }, [hold, raise]);

  const endHold = useCallback(() => {
    holding.current?.stop();
    holding.current = null;
    Animated.timing(hold, {
      toValue: 0,
      duration: 160,
      easing: Easing.out(Easing.quad),
      useNativeDriver: false,
    }).start();
  }, [hold]);

  useEffect(() => () => holding.current?.stop(), []);

  /**
   * Reopen onto an alarm that is still running.
   *
   * An incident must not disappear because the process was killed. Also drains
   * anything the queue is still holding from a previous session - a press made
   * with no signal yesterday gets delivered the moment this screen opens with
   * one.
   */
  useEffect(() => {
    const controller = new AbortController();

    (async () => {
      await drainQueue();
      const active = await fetchActive(controller.signal);
      if (!live.current || !active) {
        return;
      }
      setStage('active');
      setAlert(active);
      setDelivery(active.acknowledged ? 'acknowledged' : 'sent');
    })();

    return () => controller.abort();
  }, []);

  /**
   * While an alarm is live, keep pushing and keep asking.
   *
   * One timer doing both jobs: it drains anything still queued, and re-reads
   * the server row so an acknowledgement lands on the screen without the
   * customer touching anything. Stops as soon as the alarm is stood down.
   *
   * Deliberately not backing off. The usual reason to widen a polling interval
   * is to spare the server; the thing being spared here is somebody waiting to
   * be told a human has them.
   */
  useEffect(() => {
    if (stage !== 'active' || delivery === 'acknowledged') {
      return;
    }

    const controller = new AbortController();
    const tick = async () => {
      const sent = await drainQueue();
      if (!live.current) {
        return;
      }

      if (sent.length) {
        const mine = sent[sent.length - 1];
        setAlert(mine);
        setDelivery(mine.acknowledged ? 'acknowledged' : 'sent');
        return;
      }

      // Nothing left to push - so ask whether anyone has picked it up.
      const active = await fetchActive(controller.signal);
      if (!live.current || !active) {
        return;
      }
      setAlert(active);
      setDelivery(active.acknowledged ? 'acknowledged' : 'sent');
    };

    const timer = setInterval(tick, SOS_RETRY_MS);

    /*
     * Coming back from the background is the single most likely moment for a
     * connection to have returned - a phone that was in a lift, or had been
     * put down. Drain immediately rather than waiting out the interval.
     */
    const subscription = AppState.addEventListener('change', next => {
      if (next === 'active') {
        void tick();
      }
    });

    return () => {
      clearInterval(timer);
      subscription.remove();
      controller.abort();
    };
  }, [stage, delivery]);

  /*
   * The fill, as a width across the button rather than a circular sweep.
   *
   * `react-native-svg` could draw an arc, but an arc needs its own animated
   * prop bridge and cannot run on the native driver either. A bar that fills
   * the button from the left says the same thing - "keep holding, this is how
   * far you are" - and it cannot fail to render.
   */
  const fill = hold.interpolate({
    inputRange: [0, 1],
    outputRange: ['0%', '100%'],
  });

  return (
    <SafeAreaView style={styles.root} edges={['top', 'bottom']}>
      <View
        style={[
          styles.header,
          { paddingHorizontal: screenPadding + resp.sideInset },
        ]}
      >
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Back"
          onPress={onBack}
          hitSlop={12}
        >
          <Icon name="chevronLeft" size={22} color={colors.ink} />
        </Pressable>
        <Text variant="title" style={styles.headerTitle}>
          Safety
        </Text>
      </View>

      <ScrollView
        contentContainerStyle={[styles.page, resp.page]}
        showsVerticalScrollIndicator={false}
      >
        {stage === 'active' ? (
          // ------------------------------------------------------- raised
          <View style={styles.panel}>
            <View style={[styles.mark, styles.markDanger]}>
              {delivery === 'queued' ? (
                <ActivityIndicator color={colors.danger} />
              ) : (
                <Icon
                  name={delivery === 'acknowledged' ? 'shieldCheck' : 'siren'}
                  size={26}
                  color={colors.danger}
                />
              )}
            </View>

            {/*
              Three headings for three different truths. Only the last one
              says anybody is coming, because only the last one knows it.
            */}
            <Text variant="titleLg" align="center">
              {delivery === 'queued'
                ? 'Sending your alert'
                : delivery === 'sent'
                ? 'Alert sent'
                : 'Help is on the way'}
            </Text>
            <Text variant="bodySm" align="center" color={colors.inkBody}>
              {delivery === 'queued'
                ? 'Your phone has this and is still trying to reach us. It keeps trying, even if you close the app. If you are in immediate danger, call 112 below.'
                : delivery === 'sent'
                ? 'Our safety team has your alert and your location. Waiting for someone to pick it up - stay on this screen if you can.'
                : 'Someone on our safety team has your alert and your location. They may call you, so keep your phone to hand.'}
            </Text>

            {/*
              Standing down is one tap and carries no penalty. Nothing about a
              false alarm is counted or shown back later: hesitating to press
              the button is the failure this feature exists to prevent.
            */}
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="I am safe, stand down the alert"
              onPress={() => setStandDownOpen(true)}
              style={({ pressed }) => [
                styles.standDown,
                pressed ? styles.pressed : null,
              ]}
            >
              <Text variant="label" color={colors.ink}>
                I'm safe - stand this down
              </Text>
            </Pressable>
            <Text variant="caption" align="center" color={colors.inkMuted}>
              False alarms are completely fine. Nothing happens to your account.
            </Text>

            {/*
              ---------------------------------------------------------------
              LIVE TRACKING, AND ONLY FROM HERE
              ---------------------------------------------------------------
              Mounted only inside a car, and only once the alarm is live. Both
              halves of that are deliberate.

              Not on an ordinary trip, because a continuous position feed is a
              real cost to a phone's battery and buys almost nothing - the
              driver card's text already says what is happening, and somebody
              watching a car approach does not need a map.

              But the moment a panic button has been pressed, where the vehicle
              is *now* and which way it is moving is the entire content of the
              screen. The server knows an alert is open on this ride and
              tightens the same feed from an eight-second poll to roughly a
              second and a half; nothing here asks it to.

              Rendered even while `delivery` is still `queued`. The alarm may
              not have reached us yet, but the customer can see the car - and
              on a phone with no signal that map is the only thing on this
              screen that is still true.
            */}
            {ride ? (
              <View style={styles.tracking}>
                <View style={styles.trackingHead}>
                  <Icon name="target" size={14} color={colors.danger} />
                  <Text variant="labelSm" color={colors.danger}>
                    Following your trip live
                  </Text>
                </View>

                <RideTrackingMap rideId={ride.id} />

                {/*
                  Under the map rather than only on it: this is what somebody
                  reads out on a phone call, and a pin cannot be read out.
                */}
                {ride.driverName || ride.plate ? (
                  <Text variant="caption" color={colors.inkBody}>
                    {[ride.driverName, ride.plate].filter(Boolean).join('  •  ')}
                  </Text>
                ) : null}
              </View>
            ) : null}
          </View>
        ) : stage === 'stood_down' ? (
          // --------------------------------------------------- false alarm
          <View style={styles.panel}>
            <View style={styles.mark}>
              <Icon name="checkCircle" size={26} color={colors.primary} />
            </View>
            <Text variant="titleLg" align="center">
              Stood down
            </Text>
            <Text variant="bodySm" align="center" color={colors.inkBody}>
              Thanks for telling us. Nothing is recorded against you, and you
              can raise this again at any time.
            </Text>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Raise the alert again"
              onPress={() => setStage('idle')}
              style={({ pressed }) => [
                styles.standDown,
                pressed ? styles.pressed : null,
              ]}
            >
              <Text variant="label" color={colors.ink}>
                Back to the alarm
              </Text>
            </Pressable>
          </View>
        ) : (
          // ---------------------------------------------------- the button
          <View style={styles.panel}>
            <Text variant="titleLg" align="center">
              Feeling unsafe?
            </Text>
            <Text variant="bodySm" align="center" color={colors.inkBody}>
              Press and hold. Our safety team is alerted immediately with where
              you are and who is with you.
            </Text>

            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Hold to alert the safety team"
              accessibilityHint="Keep your finger down until the button fills"
              onPressIn={startHold}
              onPressOut={endHold}
              style={styles.trigger}
            >
              {/* Under the label, filling left to right as the hold runs. */}
              <Animated.View style={[styles.triggerFill, { width: fill }]} />
              <Icon name="siren" size={26} color={colors.onDanger} />
              <Text variant="label" color={colors.onDanger}>
                Hold to alert
              </Text>
            </Pressable>

            <Text variant="caption" align="center" color={colors.inkMuted}>
              Lift your finger to cancel. Nothing is sent until the button
              fills.
            </Text>
          </View>
        )}

        {/* ------------------------------------------------- what is sent */}
        <View style={styles.card}>
          <Text variant="labelSm" color={colors.inkBody}>
            What we send
          </Text>
          {/*
            Two lines, because they are two different things and the alarm
            carries both: where the phone is right now, and the address on the
            booking. A fix is attempted at press time and is not always
            obtainable indoors - when it is not, the address below is what ops
            works from, which is why it is never replaced by the fix.
          */}
          <Fact
            icon="pin"
            label={
              alert?.lat != null && alert?.lng != null
                ? `Your location - ${alert.lat.toFixed(5)}, ${alert.lng.toFixed(5)}`
                : 'Your location, if your phone can get it'
            }
          />
          {/*
            A ride alarm and a booking alarm carry different things, and the
            list has to say which. Inside a car the address on file is beside
            the point - the plate is what ops gives to whoever they ring.
          */}
          {ride ? (
            <>
              <Fact
                icon="target"
                label={
                  ride.plate
                    ? `Your trip and vehicle - ${ride.plate}`
                    : 'Your trip and vehicle details'
                }
              />
              {ride.driverName ? (
                <Fact icon="user" label={`Your driver, ${ride.driverName}`} />
              ) : null}
            </>
          ) : (
            <Fact icon="home" label={context.address ?? 'Your saved address'} />
          )}
          {context.service ? (
            <Fact icon="calendar" label={`${context.service} · ${context.bookingRef}`} />
          ) : null}
          {context.professional ? (
            <Fact icon="user" label={`With ${context.professional}`} />
          ) : null}
          <Fact icon="phone" label="Your number, so the team can call you" />
        </View>

        {/* ---------------------------------------------------- channels */}
        <View style={styles.card}>
          {sosChannels.map(channel => (
            <Pressable
              key={channel.key}
              accessibilityRole={channel.phone ? 'button' : 'text'}
              accessibilityLabel={`${channel.label}. ${channel.detail}`}
              disabled={!channel.phone}
              onPress={() =>
                channel.phone
                  ? Linking.openURL(`tel:${channel.phone}`).catch(() => {})
                  : undefined
              }
              style={({ pressed }) => [
                styles.channel,
                pressed ? styles.pressed : null,
              ]}
            >
              <View style={styles.mark}>
                <Icon
                  name={channel.phone ? 'phoneFilled' : 'shieldCheck'}
                  size={18}
                  color={colors.primary}
                />
              </View>
              <View style={styles.channelBody}>
                <Text variant="subtitle">{channel.label}</Text>
                <Text variant="caption" color={colors.inkMuted}>
                  {channel.detail}
                </Text>
              </View>
              {channel.phone ? (
                <Icon name="chevronRight" size={16} color={colors.borderStrong} />
              ) : null}
            </Pressable>
          ))}
        </View>
      </ScrollView>

      <ConfirmSheet
        visible={standDownOpen}
        icon="checkCircle"
        tone="neutral"
        title="Stand down the alert?"
        body="Only do this if you are safe. Our team will stop responding."
        confirmLabel="I'm safe"
        cancelLabel="Keep it active"
        onConfirm={() => {
          setStandDownOpen(false);
          /*
           * The screen moves on immediately and the request follows. Telling
           * somebody who is safe that they are still in an emergency because a
           * POST failed is worse than leaving ops a stale row to close - and
           * the row is visible to them either way.
           */
          setStage('stood_down');
          if (alert) {
            standDownAlert(alert.id).catch(() => {});
          }
          setAlert(null);
          setDelivery('queued');
        }}
        onClose={() => setStandDownOpen(false)}
      />
    </SafeAreaView>
  );
}

/** One line of what the alert carries. */
function Fact({
  icon,
  label,
}: {
  icon: React.ComponentProps<typeof Icon>['name'];
  label: string;
}) {
  return (
    <View style={styles.fact}>
      <Icon name={icon} size={15} color={colors.inkMuted} />
      <Text variant="caption" color={colors.inkBody} style={styles.grow}>
        {label}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  tracking: {
    width: '100%',
    gap: spacing.sm,
    marginTop: spacing.sm,
  },
  trackingHead: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  root: {
    flex: 1,
    backgroundColor: colors.background,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingTop: spacing.sm,
    paddingBottom: spacing.md,
    backgroundColor: colors.surface,
    borderBottomWidth: hairline.borderWidth,
    borderBottomColor: hairline.borderColor,
  },
  headerTitle: {
    flex: 1,
  },
  page: {
    /* Vertical only: the horizontal gutter comes from `resp.page` on the
       contentContainer, which also carries the tablet inset. `padding`
       here was silently overridden by it. */
    paddingVertical: screenPadding,
    gap: spacing.md,
    paddingBottom: spacing.xxxl,
  },
  panel: {
    alignItems: 'center',
    gap: spacing.sm,
    padding: spacing.xl,
    borderRadius: radii.lg,
    backgroundColor: colors.surface,
    ...hairline,
  },
  mark: {
    width: 52,
    height: 52,
    borderRadius: 26,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceAccent,
    marginBottom: spacing.xs,
  },
  markDanger: {
    backgroundColor: colors.dangerSurface,
  },
  /*
   * Deliberately the biggest control in the app.
   *
   * Nothing else here is 64pt tall, and that is the point: it has to be
   * findable by somebody who is not looking carefully, with one hand, in the
   * dark.
   */
  trigger: {
    alignSelf: 'stretch',
    height: 64,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    marginTop: spacing.lg,
    borderRadius: radii.md,
    backgroundColor: colors.danger,
    overflow: 'hidden',
  },
  /* Painted first, so the label and glyph sit over it. */
  triggerFill: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    backgroundColor: colors.primaryPressed,
    opacity: 0.35,
  },
  standDown: {
    alignSelf: 'stretch',
    alignItems: 'center',
    justifyContent: 'center',
    height: 52,
    marginTop: spacing.lg,
    borderRadius: radii.md,
    backgroundColor: colors.surfaceMuted,
  },
  card: {
    gap: spacing.sm,
    padding: spacing.lg,
    borderRadius: radii.lg,
    backgroundColor: colors.surface,
    ...hairline,
  },
  fact: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  channel: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  channelBody: {
    flex: 1,
    gap: 2,
  },
  grow: {
    flex: 1,
  },
  pressed: {
    opacity: 0.7,
  },
});

export default SosScreen;
